import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import Button from '@/components/base/Button';
import { Field, TextareaField, CheckboxField, Honeypot, FormStatus } from '@/components/base/FormFields';
import PageHero from '@/components/feature/PageHero';
import { CategoryCheckboxGroup } from '@/components/feature/CategoryFields';
import { useFormSubmit } from '@/hooks/useFormSubmit';

export default function SupplierApply() {
  const { status, submitting, errorMessage, handleSubmit } = useFormSubmit();
  const [categories, setCategories] = useState<string[]>([]);
  const [otherCategory, setOtherCategory] = useState('');
  const [localError, setLocalError] = useState('');

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    if (submitting) {
      event.preventDefault();
      return;
    }
    if (categories.length === 0) {
      event.preventDefault();
      setLocalError('Please select at least one supplier category.');
      return;
    }
    setLocalError('');
    void handleSubmit(event, 'https://readdy.ai/api/form/db6hqckbe24agvuu3jr0');
  };

  return (
    <>
      <PageHero
        eyebrow="For Suppliers"
        title="Apply to Join the KOR38 Network"
        intro="Tell us about your business. Applications are reviewed individually by Daniella."
      />

      <section className="w-full px-4 md:px-8 lg:px-12 pb-20 md:pb-28 bg-background-50">
        <div className="max-w-content mx-auto max-w-3xl">
          <form
            id="supplier_application"
            name="supplier_application"
            data-netlify="true"
            data-readdy-form="supplier_application"
            method="post"
            onSubmit={onSubmit}
            className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-8 space-y-6"
          >
            <input type="hidden" name="form-name" value="supplier_application" />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Business name" name="business_name" required autoComplete="organization" placeholder="Your business name" />
              <Field label="First name" name="first_name" required autoComplete="given-name" placeholder="Your first name" />
              <Field label="Last name" name="last_name" required autoComplete="family-name" placeholder="Your last name" />
              <Field label="Email" name="email" type="email" required autoComplete="email" placeholder="you@example.com" />
              <Field label="Telephone" name="telephone" type="tel" required autoComplete="tel" placeholder="Your contact number" />
            </div>

            <Field
              label="Website or social link (optional)"
              name="website_social"
              placeholder="https://..."
              hint="Optional. Suppliers without a website or social presence are still welcome to apply."
            />

            <CategoryCheckboxGroup
              values={categories}
              onChange={setCategories}
              otherValue={otherCategory}
              onOtherChange={setOtherCategory}
              error={localError}
            />

            <TextareaField
              label="Services offered"
              name="services_offered"
              required
              rows={4}
              placeholder="Describe what you offer and the type of events you typically work on."
            />

            <Field
              label="Coverage area"
              name="coverage_area"
              required
              placeholder="e.g. Greater London, or specific boroughs"
            />

            <TextareaField
              label="Experience"
              name="experience"
              required
              rows={4}
              placeholder="Tell us about your experience, previous events or examples of your work. If you're a newer business, we'd love to hear about your background and what you offer."
            />

            <TextareaField
              label="Insurance or qualifications (optional)"
              name="insurance_qualifications"
              rows={3}
              placeholder="Relevant insurance, accreditations or qualifications."
            />

            <Field
              label="Indicative pricing (optional)"
              name="indicative_pricing"
              placeholder="e.g. From £300 / Typically £500–£1,000 / Price on request"
              hint="Please give an approximate starting price or typical price range for your services. We understand that prices may vary depending on individual requirements."
            />

            <TextareaField
              label="Additional information (optional)"
              name="additional_info"
              rows={3}
              placeholder="Anything else you'd like us to know."
            />

            <div className="space-y-4 pt-2">
              <CheckboxField
                name="privacy_ack"
                required
                label={
                  <>
                    I have read and understand the{' '}
                    <Link to="/privacy-policy" className="text-primary-300 underline underline-offset-2">
                      Privacy Policy
                    </Link>{' '}
                    and agree to KOR38 using my details to review this application.{' '}
                    <span className="text-primary-400">*</span>
                  </>
                }
              />
              <CheckboxField
                name="marketing_consent"
                label="I would like to receive occasional news and offers from KOR38. (Optional)"
              />
            </div>

            <Honeypot />

            <div className="space-y-4">
              {localError ? (
                <p role="alert" className="text-sm text-red-300">
                  {localError}
                </p>
              ) : null}
              <FormStatus
                status={status}
                successMessage="Thank you for applying to join the KOR38 Supplier Network. Daniella will review your application and contact you if she needs any further information."
                errorMessage={errorMessage}
              />
              <Button type="submit" variant="primary" size="lg" disabled={submitting} className="w-full sm:w-auto">
                {submitting ? 'Sending…' : 'Submit My Application'}
              </Button>
            </div>
          </form>

          <p className="mt-6 text-sm leading-relaxed text-foreground-600 max-w-2xl">
            Submitting an application does not mean automatic approval, listing in a public directory,
            or automatic agreement to supplier terms.
          </p>
        </div>
      </section>
    </>
  );
}